import { Input } from '@sjuni/ui';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { FormError } from '@/components/FormError';
import { SubmitButton } from '@/components/SubmitButton';
import { applyApiErrors } from '@/lib/forms/applyApiErrors';
import { resetPassword } from '@/lib/auth/api';

interface ResetFields {
  password: string;
  password_confirmation: string;
}

/**
 * docs/04-API-SPEC.md §5 — `POST /admin/auth/reset-password`.
 *
 * The token and address come from the emailed link. Neither is editable here:
 * the token is only valid for the address it was issued against, so a form that
 * let one be changed would only ever produce a confusing failure.
 */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();

  const token = params.get('token') ?? '';
  const email = params.get('email') ?? '';

  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ResetFields>({ defaultValues: { password: '', password_confirmation: '' } });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);

    try {
      await resetPassword({ token, email, ...values });
      setDone(true);
    } catch (error) {
      const { formError: message, placed } = applyApiErrors(error, setError, [
        'password',
        'password_confirmation',
      ]);

      setFormError(message ?? (placed ? null : t('errors.generic')));
    }
  });

  if (token === '' || email === '') {
    return (
      <div className="space-y-4">
        <FormError message={t('auth.reset.badLink')} />
        <p className="text-body-sm">
          <Link to="/forgot-password" className="text-link underline-offset-2 hover:underline">
            {t('auth.reset.requestAnother')}
          </Link>
        </p>
      </div>
    );
  }

  if (done) {
    return (
      <div className="space-y-4">
        <p role="status" className="text-body-sm text-secondary">
          {t('auth.reset.done')}
        </p>
        <p className="text-body-sm">
          <Link to="/login" className="text-link underline-offset-2 hover:underline">
            {t('auth.backToLogin')}
          </Link>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5">
      <FormError message={formError} />

      {/* Shown, not editable — it is what the token was issued for. */}
      <p className="text-body-sm text-secondary">{t('auth.reset.forAccount', { email })}</p>

      <Input
        {...register('password', { required: t('auth.validation.passwordRequired') })}
        type="password"
        autoComplete="new-password"
        /* eslint-disable-next-line jsx-a11y/no-autofocus -- a screen whose only
           purpose is this one form; the rule guards against stealing focus
           mid-page, which is not what this is. */
        autoFocus
        label={t('auth.fields.newPassword')}
        hint={t('auth.reset.passwordHint')}
        error={errors.password?.message}
        required
      />

      <Input
        {...register('password_confirmation', {
          required: t('auth.validation.confirmRequired'),
        })}
        type="password"
        autoComplete="new-password"
        label={t('auth.fields.confirmPassword')}
        error={errors.password_confirmation?.message}
        required
      />

      <SubmitButton fullWidth loading={isSubmitting}>
        {t('auth.reset.submit')}
      </SubmitButton>
    </form>
  );
}
